import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChangeSubscriptionLimits } from '../../../shared/models/plans';
import { InboundHandlingStrategyType } from '../../../account/account.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'cc-change-plan-confirmation',
  templateUrl: './change-plan-confirmation.component.html',
  styleUrls: ['./change-plan-confirmation.component.scss']
})
export class ChangePlanConfirmationComponent implements OnInit {
  public differences = [];
  public differencesToShow = [
    'outboundRestricted',
    'callsHistoryLimit',
    'recordingsStorageDays',
    'ON_HOLD_MESSAGE_PERMISSION',
    'TOLL_FREE_NUMBERS_PERMISSION',
    'VOICE_MAIL_PERMISSION',
    'AGENT_LISTENING_PERMISSION',
    'VOICE_MAIL_DROP_PERMISSION',
    'TAGS_PERMISSION',
    'CALL_FORWARDING_PERMISSION',
    'UNLIMITED_MINUTES_PERMISSION',
  ];
  public unlimitedMinutes = 'UNLIMITED_MINUTES_PERMISSION'
  public overLimits = [];
  public featureLinks = {
    ivrs: '/ivr',
    dids: '/my-account/plan',
    integrations: '/integrations',
    inboundStrategies: '/phone-numbers'
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { accountLimits: ChangeSubscriptionLimits, plan },
    private dialogRef: MatDialogRef<ChangePlanConfirmationComponent>,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.overLimits = this.checkOverLimitsValues();
    this.differences = this.checkMetaDataDifferences();
    if (!this.overLimits.length && !this.differences.length) {

      this.closeDialog(true);
    }
  }

  private checkMetaDataDifferences() {
    const rawDifferences = Object.keys(this.data.accountLimits.metaDataDifference).map(key => ({
      key,
      value: this.data.accountLimits.metaDataDifference[key]
    }));
    const permissionDifference = this.data.accountLimits.metaDataDifference.extraPermissions
      ? (this.data.accountLimits.metaDataDifference.extraPermissions as string[]).map(permission => ({
          key: permission,
          value: permission
        }))
      : [];
    if(this.data.plan.licenses[0].planName === "Advanced"){
      permissionDifference.push({
        key: this.unlimitedMinutes,
        value: this.unlimitedMinutes
        }
      );
    }
    return [...rawDifferences, ...permissionDifference].filter(diff => this.differencesToShow.includes(diff.key));
  }

  private checkOverLimitsValues() {
    const extractFeatureFromProperty = prop => prop.replace('numberOf', '').toLowerCase();

    const rawOverLimits = Object.keys(this.data)
      .filter(key => key.startsWith('numberOf'))
      .map(key => {
        const feature = extractFeatureFromProperty(key);
        return {
          feature,
          current: this.data[key],
          limit: this.data[`${feature}Limit`],
          link: this.featureLinks[feature]
        };
      })
      .filter(featureOverLimit => featureOverLimit.limit !== null && featureOverLimit.current > featureOverLimit.limit)
      .filter(featureOverLimit => featureOverLimit.feature !== 'dids' || !this.data.accountLimits.outboundRestricted);

    const invalidInboundStrategies = this.data.accountLimits.currentInboundStrategies.filter(
      currentStrategy => !this.data.accountLimits.availableInboundStrategies.includes(currentStrategy)
    );

    if (!invalidInboundStrategies.length) {
      return rawOverLimits;
    }
    return [...rawOverLimits, this.inboundStrategyFeature(invalidInboundStrategies)];
  }

  closeDialog(result: boolean = false) {
    this.dialogRef.close(result);
  }

  canProceed(): boolean {
    return !this.overLimits.filter(featureOverLimit => !this.autoIntegrationRemoval(featureOverLimit)).length;
  }

  autoIntegrationRemoval(overLimit): boolean {
    return (
      overLimit.feature === 'integrations' && this.data.accountLimits.integrationsLimit === 0 && this.data.accountLimits.numberOfIntegrations > 0
    );
  }

  private inboundStrategyFeature(invalidInboundStrategies: InboundHandlingStrategyType[]) {
    const or = this.translateService.instant('OR');

    return {
      feature: 'inboundStrategies',
      current: invalidInboundStrategies
        .map(strategy => `"${this.translateService.instant(`INBOUND_STRATEGIES.${strategy}`)}"`)
        .join(` ${or} `),
      limit: this.data.accountLimits.availableInboundStrategies
        .map(strategy => `"${this.translateService.instant(`INBOUND_STRATEGIES.${strategy}`)}"`)
        .join(` ${or} `),
      link: this.featureLinks.inboundStrategies
    };
  }
}
